import type { Href } from 'expo-router';
import { ChevronRight, ExternalLink, Package } from 'lucide-react-native';
import { Pressable, StyleSheet, View } from 'react-native';

import { Text } from '@/components/Text';
import { colors, fontSizes, radii, spacing } from '@/constants/theme';
import { usePushOnce } from '@/hooks/usePushOnce';
import { openWebUrl } from '@/lib/openWebUrl';
import type { ShippingTracker as Tracker } from '@/types/deal';

import { formatStageDay } from './TrackTimeline';

export type TrackPackageCardProps = {
  tracking: NonNullable<Tracker['tracking']>;
  trackHref?: Href;
  testID?: string;
};

/**
 * The carrier card: tap opens the Track package page, or (without one) the
 * carrier's tracking page (http(s) only).
 */
export function TrackPackageCard({ tracking, trackHref, testID }: TrackPackageCardProps) {
  const { url } = tracking;
  const title = trackingTitle(tracking);
  const pushOnce = usePushOnce();
  const onPress = trackHref ? () => pushOnce(trackHref) : url ? () => openWebUrl(url) : undefined;
  return (
    <Pressable
      onPress={onPress}
      disabled={!onPress}
      accessibilityRole={trackHref ? 'button' : url ? 'link' : undefined}
      accessibilityLabel={`${title}, ${tracking.detail}`}
      accessibilityHint={trackHref ? 'Opens the package tracking page' : undefined}
      style={({ pressed }) => [styles.card, pressed && styles.cardPressed]}
      testID={testID}
    >
      <View style={styles.iconTile}>
        <Package size={22} color={colors.deepGreen} strokeWidth={2} />
      </View>
      <View style={styles.cardBody}>
        <Text variant="bodyBold" color="deepGreen">
          {title}
        </Text>
        {/* 12pt, up to two lines: a 22-digit USPS number wraps under the carrier instead of losing digits. */}
        <Text variant="captionBold" color="textMuted" numberOfLines={2} style={styles.detail}>
          {tracking.detail}
        </Text>
      </View>
      {trackHref ? <ChevronRight size={20} color={colors.deepGreen} strokeWidth={2} /> : null}
      {!trackHref && url ? <ExternalLink size={20} color={colors.deepGreen} strokeWidth={2} /> : null}
    </Pressable>
  );
}

const ICON_TILE = 48;

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.border,
    // White on the near-white page, like the page's other cards.
    backgroundColor: colors.surface,
  },
  cardPressed: {
    opacity: 0.7,
  },
  iconTile: {
    width: ICON_TILE,
    height: ICON_TILE,
    borderRadius: radii.base,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.brightGreenSoft,
  },
  cardBody: {
    flex: 1,
    gap: 2,
  },
  detail: {
    fontSize: fontSizes.xs,
    lineHeight: 16,
  },
});

/**
 * "Shipped Oct 20" dated in the phone's zone, the same day the Track package
 * timeline shows; the server's (UTC) title when it sent no time.
 */
function trackingTitle({ title, verb, at }: TrackPackageCardProps['tracking']): string {
  const day = verb && at ? formatStageDay(at) : '';
  return day ? `${verb} ${day}` : title;
}
